// AuthClient — auth-api SDK.
// 두 레포 (portal, admin) 가 동일 파일 복제 사용. Phase 5 에서 사내 패키지 분리.

import type {
  ApiError,
  JoinRequest,
  MeResponse,
  MfaChallengeResponse,
  OrganizationSearchItem,
  TokenResponse,
  UserSiteMembership,
} from './types';

export interface AuthClientConfig {
  baseUrl: string;
}

export class AuthApiError extends Error {
  status: number;
  code: string;
  detail: ApiError;

  constructor(status: number, detail: ApiError) {
    super(detail.code);
    this.status = status;
    this.code = detail.code;
    this.detail = detail;
  }
}

const ACCESS_KEY = 'ontoh.auth.access';
const REFRESH_KEY = 'ontoh.auth.refresh';
const ORG_KEY = 'ontoh.auth.current_org_id';

export class AuthClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshing: Promise<void> | null = null;

  constructor(config: AuthClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.accessToken = localStorage.getItem(ACCESS_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_KEY);
  }

  // ─── 토큰 ────────────────────────────────────────

  isAuthenticated(): boolean {
    return Boolean(this.accessToken);
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  getCurrentOrgId(): string | null {
    return localStorage.getItem(ORG_KEY);
  }

  setCurrentOrgId(orgId: string | null): void {
    if (orgId) localStorage.setItem(ORG_KEY, orgId);
    else localStorage.removeItem(ORG_KEY);
  }

  private setTokens(access: string, refresh: string): void {
    this.accessToken = access;
    this.refreshToken = refresh;
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ORG_KEY);
  }

  // ─── HTTP ────────────────────────────────────────

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    skipRefresh = false,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.accessToken)
      headers['Authorization'] = `Bearer ${this.accessToken}`;

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !skipRefresh && this.refreshToken) {
      try {
        await this.attemptRefresh();
        return this.request<T>(method, path, body, true);
      } catch {
        this.clearTokens();
        throw new AuthApiError(401, { code: 'session_expired' });
      }
    }

    if (res.status === 204) return undefined as T;

    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      const detail =
        (data.detail as ApiError | undefined) ?? (data as ApiError);
      throw new AuthApiError(res.status, detail);
    }
    return data as T;
  }

  private async attemptRefresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      const res = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
      if (!res.ok) {
        throw new Error('refresh_failed');
      }
      const data = (await res.json()) as TokenResponse;
      this.setTokens(data.access_token, data.refresh_token);
    })();
    try {
      await this.refreshing;
    } finally {
      this.refreshing = null;
    }
  }

  // ─── 인증 ────────────────────────────────────────

  async login(
    identifier: string,
    password: string,
  ): Promise<TokenResponse | MfaChallengeResponse> {
    const data = await this.request<
      TokenResponse | MfaChallengeResponse
    >('POST', '/v1/auth/login', { identifier, password }, true);
    if ('mfa_required' in data && data.mfa_required) {
      // 토큰 미발급 — 호출자가 /v1/auth/login/mfa 로 challenge 응답
      return data;
    }
    const tokens = data as TokenResponse;
    this.setTokens(tokens.access_token, tokens.refresh_token);
    return tokens;
  }

  /** Phase 5.5b — login → mfa_required challenge 응답 후 OTP/backup 으로 토큰 발급. */
  async loginMfa(challengeId: string, code: string): Promise<TokenResponse> {
    const data = await this.request<TokenResponse>(
      'POST',
      '/v1/auth/login/mfa',
      { challenge_id: challengeId, code },
      true,
    );
    this.setTokens(data.access_token, data.refresh_token);
    return data;
  }

  // ─── MFA TOTP (Phase 5.5) ────────────────────────

  // Phase 5c.C — method/recipient 분기. body 없으면 기본 'totp' (기존 호출자 호환).
  async mfaSetup(
    body: { method?: string; recipient?: string } = {},
  ): Promise<{
    method: string;
    secret: string | null;
    otpauth_uri: string | null;
    recipient_hint: string | null;
  }> {
    return this.request<{
      method: string;
      secret: string | null;
      otpauth_uri: string | null;
      recipient_hint: string | null;
    }>('POST', '/v1/auth/mfa/setup', body);
  }

  async mfaEnable(code: string): Promise<{ backup_codes: string[] }> {
    return this.request<{ backup_codes: string[] }>(
      'POST',
      '/v1/auth/mfa/enable',
      { code },
    );
  }

  async mfaVerify(code: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      '/v1/auth/mfa/verify',
      { code },
    );
  }

  async mfaRegenerateBackupCodes(code: string): Promise<{ backup_codes: string[] }> {
    return this.request<{ backup_codes: string[] }>(
      'POST',
      '/v1/auth/mfa/backup-codes',
      { code },
    );
  }

  async mfaDisable(): Promise<void> {
    await this.request<void>('DELETE', '/v1/auth/mfa');
  }

  async signup(input: {
    identifier: { email?: string; person_id?: string };
    password: string;
    name: string;
    phone?: string;
    organization_id?: string;
  }): Promise<{ user: { id: string }; access_token: string; refresh_token: string }> {
    const data = await this.request<{
      user: { id: string };
      access_token: string;
      refresh_token: string;
      expires_in: number;
    }>('POST', '/v1/users/signup', input, true);
    this.setTokens(data.access_token, data.refresh_token);
    return data;
  }

  async logout(): Promise<void> {
    try {
      await this.request<void>('POST', '/v1/auth/logout', {
        refresh_token: this.refreshToken,
      });
    } catch {
      // 무시 — 클라이언트는 어쨌든 로컬 토큰 삭제
    }
    this.clearTokens();
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      '/v1/auth/forgot-password',
      { email },
      true,
    );
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      '/v1/auth/reset-password',
      { token, new_password: newPassword },
      true,
    );
  }

  async getMe(): Promise<MeResponse> {
    return this.request<MeResponse>('GET', '/v1/auth/me');
  }

  async updateMe(data: { name?: string; phone?: string }): Promise<MeResponse> {
    return this.request<MeResponse>('PUT', '/v1/auth/me', data);
  }

  async changePassword(current: string, next: string): Promise<void> {
    await this.request<void>('PUT', '/v1/auth/me/password', {
      current_password: current,
      new_password: next,
    });
  }

  // ─── 무소속 재가입 ────────────────────────────────

  async searchOrganizations(
    q: string,
    type?: string,
    limit = 20,
  ): Promise<OrganizationSearchItem[]> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (type) params.set('org_type', type);
    const data = await this.request<{ items: OrganizationSearchItem[] }>(
      'GET',
      `/v1/organizations/search?${params}`,
    );
    return data.items;
  }

  async requestJoin(
    organizationId: string,
    message?: string,
    requestedRole = 'org_member',
  ): Promise<JoinRequest> {
    return this.request<JoinRequest>('POST', '/v1/organization-join-requests', {
      organization_id: organizationId,
      requested_role: requestedRole,
      message,
    });
  }

  async myJoinRequests(): Promise<JoinRequest[]> {
    const data = await this.request<{ items: JoinRequest[] }>(
      'GET',
      '/v1/organization-join-requests?user_id=me',
    );
    return data.items;
  }

  async cancelJoinRequest(id: string): Promise<JoinRequest> {
    return this.request<JoinRequest>(
      'POST',
      `/v1/organization-join-requests/${id}/cancel`,
    );
  }

  // Phase 5c — org_admin 인 사용자가 자기 조직 가입 요청 처리.
  // BE 가 organization_id 기반 권한 검사 (can_manage_org).
  async listJoinRequestsForOrg(orgId: string, status?: string): Promise<JoinRequest[]> {
    const sp = new URLSearchParams({ organization_id: orgId });
    if (status) sp.set('status', status);
    const data = await this.request<{ items: JoinRequest[] }>(
      'GET',
      `/v1/organization-join-requests?${sp}`,
    );
    return data.items;
  }

  async approveJoinRequest(id: string, override_role?: string) {
    return this.request<{ join_request: JoinRequest; membership_created: unknown }>(
      'POST',
      `/v1/organization-join-requests/${id}/approve`,
      override_role ? { override_role } : {},
    );
  }

  async rejectJoinRequest(id: string, reason: string) {
    return this.request<JoinRequest>(
      'POST',
      `/v1/organization-join-requests/${id}/reject`,
      { rejection_reason: reason },
    );
  }

  async listMySiteMemberships(): Promise<UserSiteMembership[]> {
    const data = await this.request<{ items: UserSiteMembership[] }>(
      'GET',
      '/v1/user-site-memberships?user_id=me',
    );
    return data.items;
  }
}

let _instance: AuthClient | null = null;

export function getAuthClient(): AuthClient {
  if (_instance) return _instance;
  const baseUrl =
    (import.meta as ImportMeta & { env: { VITE_AUTH_API_URL?: string } }).env
      .VITE_AUTH_API_URL ?? 'http://localhost:8001';
  _instance = new AuthClient({ baseUrl });
  return _instance;
}
